// @ts-nocheck
import { createService } from "@/integration/factory/service-factory.ts";
import {
  ICreateTeamAchievementRequest,
  ITeamAchievement,
  ITeamAchievementPaginated,
  IUpdateTeamAchievementRequest,
} from "../interfaces/team-achievements.interface";

/**
 * Service for team achievements-related API calls
 */
export default function teamAchievementsService() {
  // Create base services for team achievements endpoints
  const teamsService = createService("/teams");

  /**
   * Get team achievements with pagination
   * @param teamId The team ID
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 10)
   * @returns Paginated team achievements
   */
  const getTeamAchievements = async (
    teamId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<ITeamAchievementPaginated> => {
    try {
      const params = { page, limit };
      return await teamsService.customGet<ITeamAchievementPaginated>(
        `/${teamId}/achievements`,
        { params }
      );
    } catch (error) {
      console.error("Error fetching team achievements:", error);
      return {
        data: [],
        page,
        limit,
        total: 0,
        totalPage: 0,
      } as ITeamAchievementPaginated;
    }
  };

  /**
   * Get a specific team achievement by ID
   * @param teamId The team ID
   * @param achievementId The achievement ID
   * @returns The team achievement
   */
  const getTeamAchievementById = async (
    teamId: string,
    achievementId: string
  ): Promise<ITeamAchievement | null> => {
    try {
      return await teamsService.customGet<ITeamAchievement>(
        `/${teamId}/achievements/${achievementId}`
      );
    } catch (error) {
      console.error("Error fetching team achievement:", error);
      return null;
    }
  };

  /**
   * Create a team achievement
   * @param teamId The team ID
   * @param data The achievement data
   * @returns The created team achievement
   */
  const createTeamAchievement = async (
    teamId: string,
    data: ICreateTeamAchievementRequest
  ): Promise<ITeamAchievement | null> => {
    try {
      return await teamsService.customPost<ITeamAchievement>(
        `/${teamId}/achievements`,
        { ...data, team_id: teamId }
      );
    } catch (error) {
      console.error("Error creating team achievement:", error);
      return null;
    }
  };

  /**
   * Update a team achievement
   * @param teamId The team ID
   * @param achievementId The achievement ID
   * @param data The achievement data
   * @returns The updated team achievement
   */
  const updateTeamAchievement = async (
    teamId: string,
    achievementId: string,
    data: IUpdateTeamAchievementRequest
  ): Promise<ITeamAchievement | null> => {
    try {
      return await teamsService.customPut<ITeamAchievement>(
        `/${teamId}/achievements/${achievementId}`,
        data
      );
    } catch (error) {
      console.error("Error updating team achievement:", error);
      return null;
    }
  };

  /**
   * Delete a team achievement
   * @param teamId The team ID
   * @param achievementId The achievement ID
   * @returns Success status
   */
  const deleteTeamAchievement = async (
    teamId: string,
    achievementId: string
  ): Promise<boolean> => {
    try {
      await teamsService.customDelete(
        `/${teamId}/achievements/${achievementId}`
      );
      return true;
    } catch (error) {
      console.error("Error deleting team achievement:", error);
      return false;
    }
  };

  return {
    getTeamAchievements,
    getTeamAchievementById,
    createTeamAchievement,
    updateTeamAchievement,
    deleteTeamAchievement,
  };
}